import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Spinner,
  Alert,
  Badge,
  Form
} from 'react-bootstrap';
import {
  IconFileText,
  IconCopy,
  IconAlertTriangle,
  IconClock,
  IconDatabase,
  IconRobot,
  IconMaximize,
  IconMinimize
} from '@tabler/icons-react';
import { getAgentPrompt } from '../../../../services/AgentsServices';

const AgentPromptModal = ({ 
  show, 
  onHide, 
  agentContext = null 
}) => {
  // Estados locais
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [promptData, setPromptData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Reset ao fechar
  useEffect(() => {
    if (!show) {
      setPromptData(null);
      setError(null);
      setCopied(false);
      setIsExpanded(false);
    }
  }, [show]);

  // Carregar prompt quando modal abrir
  useEffect(() => {
    if (show && agentContext?.id) {
      loadAgentPrompt();
    }
  }, [show, agentContext]);

  // Função para carregar prompt do agente
  const loadAgentPrompt = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await getAgentPrompt(agentContext.id);
      
      if (result.success) {
        setPromptData(result.data);
      } else {
        setError(result.error || 'Erro ao carregar prompt do agente');
      }
    } catch (err) {
      console.error('Erro ao carregar prompt:', err);
      setError('Erro inesperado ao carregar prompt');
    } finally {
      setLoading(false);
    }
  };

  // Copiar prompt
  const handleCopy = () => {
    if (promptData?.prompt) {
      navigator.clipboard.writeText(promptData.prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Formatar informações do cache
  const getCacheInfo = () => {
    if (!promptData) return null;

    const sourceLabels = {
      'cache': 'Cache',
      'fallback': 'Fallback (DynamicPromptBuilder)'
    };

    const modeLabels = {
      'internal_playground': 'Playground Interno'
    };

    return {
      source: sourceLabels[promptData.source] || promptData.source,
      mode: modeLabels[promptData.mode] || promptData.mode,
      cacheKey: promptData.cache_key
    };
  };

  const cacheInfo = getCacheInfo();
  const promptLength = promptData?.prompt?.length || 0;
  const promptWords = promptData?.prompt ? promptData.prompt.split(/\s+/).length : 0;

  return (
    <Modal 
      show={show} 
      onHide={onHide}
      size="lg"
      centered
      className="prompt-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <IconFileText size={20} />
          Prompt do Agente
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {/* Info do Agente */}
        {agentContext && (
          <Alert variant="light" className="d-flex align-items-center gap-2 mb-3">
            <IconRobot size={16} />
            <div>
              <strong>Agente:</strong> {agentContext.agent_name}
              {agentContext.agent_job && (
                <Badge bg="secondary" className="ms-2">
                  {agentContext.agent_job}
                </Badge>
              )}
            </div>
          </Alert>
        )}

        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 mb-0">Carregando prompt...</p>
          </div>
        )}

        {error && (
          <Alert variant="danger" className="d-flex align-items-center gap-2">
            <IconAlertTriangle size={16} />
            {error}
          </Alert>
        )}

        {promptData && !loading && (
          <>
         
            {/* Botões de Ação */}
            <div className="d-flex gap-2 mb-3">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setIsExpanded(true)}
                className="d-flex align-items-center gap-1"
                title="Expandir para tela cheia"
              >
                <IconMaximize size={14} />
                Expandir
              </Button>
              
              <Button
                variant={copied ? "success" : "outline-secondary"}
                size="sm"
                onClick={handleCopy}
                className={`d-flex align-items-center gap-1 ${copied ? 'copied' : ''}`}
              >
                <IconCopy size={14} />
                {copied ? 'Copiado!' : 'Copiar'}
              </Button>

              <Button
                variant="outline-info"
                size="sm"
                onClick={loadAgentPrompt}
                disabled={loading}
                className="d-flex align-items-center gap-1"
                title="Recarregar prompt"
              >
                {loading ? <Spinner size="sm" /> : <IconDatabase size={14} />}
                Recarregar
              </Button>
            </div>

            {/* Prompt Content */}
            <div className="mb-3">
              <h6 className="mb-2">Conteúdo do Prompt</h6>
              <Form.Control
                as="textarea"
                value={promptData.prompt || ''}
                readOnly
                rows={12}
                style={{
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontFamily: 'monospace',
                  fontSize: '13px',
                  lineHeight: '1.6',
                  resize: 'vertical',
                  minHeight: '300px'
                }}
                placeholder="Nenhum prompt encontrado"
              />
            </div>

            {/* Modal de Expansão */}
            <Modal
              show={isExpanded}
              onHide={() => setIsExpanded(false)}
              size="xl"
              fullscreen
              className="prompt-expanded-modal"
            >
              <Modal.Header closeButton style={{ borderBottom: "2px solid #e2e8f0" }}>
                <Modal.Title className="d-flex align-items-center gap-2">
                  <IconFileText size={20} />
                  Prompt do Agente - Visualização Expandida
                </Modal.Title>
              </Modal.Header>
              <Modal.Body style={{ padding: "2rem" }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <IconRobot size={16} className="text-primary" />
                    <span className="fw-bold">Agente: {agentContext?.agent_name || 'Não identificado'}</span>
                    <Badge bg="info">{promptLength.toLocaleString()} caracteres</Badge>
                    <Badge bg="secondary">{promptWords} palavras</Badge>
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      variant={copied ? "success" : "outline-secondary"}
                      onClick={handleCopy}
                      className={`d-flex align-items-center gap-1 ${copied ? "copied" : ""}`}
                    >
                      <IconCopy size={16} />
                      {copied ? "Copiado!" : "Copiar Prompt"}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={() => setIsExpanded(false)}
                      className="d-flex align-items-center gap-1"
                    >
                      <IconMinimize size={16} />
                      Minimizar
                    </Button>
                  </div>
                </div>
                <div
                  className="prompt-expanded-content"
                  style={{
                    backgroundColor: "#ffffff",
                    border: "2px solid #e2e8f0",
                    borderRadius: "12px",
                    padding: "2rem",
                    fontSize: "14px",
                    lineHeight: "1.7",
                    fontFamily: "monospace",
                    whiteSpace: "pre-wrap",
                    height: "calc(100vh - 200px)",
                    overflowY: "auto",
                    boxShadow: "inset 0 2px 8px rgba(0,0,0,0.05)"
                  }}
                >
                  {promptData.prompt || 'Nenhum prompt disponível'}
                </div>
              </Modal.Body>
            </Modal>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default AgentPromptModal;